import {Box} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import {User, UserType, USER_MANAGEMENT_USER_TYPES} from "crqlar-react-common/dist/models/identity/user.model";
import {ClientUserService} from "crqlar-react-common/dist/services/user/clientUser.service";
import {MyAccountService} from "crqlar-react-common/dist/services/identity/myAccount.service";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../common/serverTable";
import SettingsListPage from "../system/layout/SettingsListPage";

const UsersList = () => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const organization = useOrganization();

    const myAccountDataQ = useQuery(['myAccount'], () => MyAccountService.getMyAccount());
    const disableAddUser = !USER_MANAGEMENT_USER_TYPES
        .includes(myAccountDataQ.data?.userType as UserType);

    const columns = useMemo<ColumnDef<User>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: user => [user.firstName, user.lastName].filter(Boolean).join(' '),
            cell: ({row}) => <Box>{[row.original.firstName, row.original.lastName].filter(Boolean).join(' ')}</Box>,
            size: 260,
            meta: {defaultActive: true, grow: true}
        },
        {
            id: 'email',
            header: t('Email'),
            accessorFn: user => user.email,
            cell: ({row}) => <Box>{row.original.email || '-'}</Box>,
            size: 360,
            meta: {defaultActive: true}
        },
        {
            id: 'type',
            header: t('Type'),
            accessorFn: user => user.userType,
            cell: ({row}) => (
                <Box>{row.original.userType ? t(`User Type ${row.original.userType}`) : t('(Not set)')}</Box>
            ),
            size: 260,
            meta: {defaultActive: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: user => user.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: user => user.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId: 'system-team',
        columns,
        getRowId: user => user.id!,
        onRowClick: user => navigate(`/system/team/${user.id}`),
        initialSorting: [{id: 'updateDate', desc: true}],
        queryDependencies: [organization?.id],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            const page = await ClientUserService.getAllUsers({
                query: searchQuery || undefined,
                organizationId: organization?.id,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });
            return {content: page.content ?? [], totalElements: page.totalElements ?? 0};
        }
    });

    return (
        <SettingsListPage title={t('Team')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate
                            label={t('Add user')}
                            onClick={() => navigate('/system/team/new')}
                            disabled={disableAddUser}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default UsersList;
